import { CHAT_MAX_LENGTH, sanitizeChatText, type ChatMessage, type ChatScope } from '../../shared/src/index.ts';

import { t, tr } from './i18n.ts';
/** Segundos que un mensaje sigue visible en partida con el chat cerrado. */
const GAME_LINE_SECONDS = 10;
/** Mensajes que se conservan en el cliente. */
const MAX_MESSAGES = 80;

export interface ChatOptions {
  /** envía un mensaje (online: al servidor; offline: eco local) */
  send: (text: string, scope: ChatScope) => void;
  /** si Enter puede abrir el chat de partida ahora (en juego, sin menús abiertos) */
  canOpenInGame: () => boolean;
}

/**
 * Chat de la sala y de la partida. En el lobby es un panel fijo con su campo de
 * texto; en partida vive abajo a la derecha (sobre el minimapa): Enter lo abre,
 * Enter envía y cierra, Esc cierra, Tab alterna entre todos y equipo
 * (Shift+Enter lo abre directamente en equipo). Con el chat cerrado los mensajes
 * se ven unos segundos y se desvanecen.
 */
export class ChatUi {
  private messages: ChatMessage[] = [];
  private readonly seen = new Set<number>();
  /** cuándo llegó cada mensaje (para desvanecerlo en partida) */
  private readonly arrivedAt = new WeakMap<ChatMessage, number>();
  private localId = -1;
  private scope: ChatScope = 'all';
  private gameOpen = false;

  private readonly lobbyRoot = el('lobby-chat');
  private readonly lobbyLog = el('lobby-chat-log');
  private readonly lobbyInput = el<HTMLInputElement>('lobby-chat-input');
  private readonly gameRoot = el('game-chat');
  private readonly gameLog = el('game-chat-log');
  private readonly gameRow = el('game-chat-row');
  private readonly gameInput = el<HTMLInputElement>('game-chat-input');
  private readonly gameScope = el('game-chat-scope');

  constructor(private readonly options: ChatOptions) {
    for (const input of [this.lobbyInput, this.gameInput]) {
      input.maxLength = CHAT_MAX_LENGTH;
      // las teclas escritas no llegan a los atajos del juego (QWER, B, P, M, I, O...)
      input.addEventListener('keydown', (event) => event.stopPropagation());
    }

    this.lobbyInput.addEventListener('keydown', (event) => {
      if (event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
      event.preventDefault();
      this.submit(this.lobbyInput, 'all');
    });
    el('lobby-chat-send').addEventListener('click', () => {
      this.submit(this.lobbyInput, 'all');
      this.lobbyInput.focus();
    });

    this.gameInput.addEventListener('keydown', (event) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        this.submit(this.gameInput, this.scope);
        this.closeGame();
      } else if (event.code === 'Escape') {
        event.preventDefault();
        this.closeGame();
      } else if (event.code === 'Tab') {
        event.preventDefault();
        this.setScope(this.scope === 'all' ? 'team' : 'all');
      }
    });
    this.gameInput.addEventListener('blur', () => this.closeGame());
    this.gameScope.addEventListener('mousedown', (event) => {
      event.preventDefault(); // no robar el foco al campo
      this.setScope(this.scope === 'all' ? 'team' : 'all');
    });

    window.addEventListener('keydown', (event) => {
      if (event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
      if (this.gameOpen || event.repeat || event.ctrlKey || event.altKey || event.metaKey) return;
      if (document.activeElement instanceof HTMLInputElement || !this.options.canOpenInGame()) return;
      event.preventDefault();
      this.openGame(event.shiftKey ? 'team' : 'all');
    });

    window.setInterval(() => this.refreshGameFades(), 500);
  }

  /** Muestra u oculta el panel del lobby (sólo online: offline no hay con quién hablar). */
  setLobbyVisible(visible: boolean): void {
    this.lobbyRoot.hidden = !visible;
  }

  /** Muestra u oculta el chat de partida (la capa de juego). */
  setGameVisible(visible: boolean): void {
    this.gameRoot.hidden = !visible;
    if (!visible) this.closeGame();
  }

  /** Borra la conversación (al salir de la sala o empezar otra partida offline). */
  clear(): void {
    this.messages = [];
    this.seen.clear();
    this.render();
  }

  /** Añade mensajes recibidos (los repetidos por id se ignoran). */
  push(messages: readonly ChatMessage[]): void {
    let added = false;
    for (const message of messages) {
      if (!message || typeof message.text !== 'string' || this.seen.has(message.id)) continue;
      this.seen.add(message.id);
      this.messages.push(message);
      this.arrivedAt.set(message, performance.now());
      added = true;
    }
    if (!added) return;
    if (this.messages.length > MAX_MESSAGES) this.messages.splice(0, this.messages.length - MAX_MESSAGES);
    this.render();
  }

  /** Mensaje local (offline o avisos del cliente). */
  pushLocal(name: string, text: string, scope: ChatScope, team: ChatMessage['team'], system = false): void {
    this.push([{ id: this.localId--, name, text, scope, team, from: 'local', system: system || undefined }]);
  }

  get isTyping(): boolean {
    return this.gameOpen;
  }

  private submit(input: HTMLInputElement, scope: ChatScope): void {
    const text = sanitizeChatText(input.value);
    input.value = '';
    if (text) this.options.send(text, scope);
  }

  private openGame(scope: ChatScope): void {
    this.gameOpen = true;
    this.setScope(scope);
    this.gameRoot.classList.add('open');
    this.gameRow.hidden = false;
    this.gameInput.focus();
    this.refreshGameFades();
    this.gameLog.scrollTop = this.gameLog.scrollHeight;
  }

  private closeGame(): void {
    if (!this.gameOpen) return;
    this.gameOpen = false;
    this.gameRoot.classList.remove('open');
    this.gameRow.hidden = true;
    if (document.activeElement === this.gameInput) this.gameInput.blur();
    this.refreshGameFades();
    this.gameLog.scrollTop = this.gameLog.scrollHeight;
  }

  private setScope(scope: ChatScope): void {
    this.scope = scope;
    this.gameScope.textContent = scope === 'team' ? t('[EQUIPO]') : t('[TODOS]');
    this.gameScope.className = `chat-scope ${scope}`;
    this.gameInput.placeholder = scope === 'team' ? t('Mensaje a tu equipo… (Tab: todos)') : t('Mensaje a todos… (Tab: equipo)');
  }

  private render(): void {
    this.renderLog(this.lobbyLog, false);
    this.renderLog(this.gameLog, true);
  }

  private renderLog(log: HTMLElement, game: boolean): void {
    const stick = log.scrollTop + log.clientHeight >= log.scrollHeight - 8;
    log.replaceChildren(...this.messages.map((message) => this.line(message, game)));
    if (stick || game) log.scrollTop = log.scrollHeight;
    if (game) this.refreshGameFades();
  }

  private line(message: ChatMessage, game: boolean): HTMLElement {
    const row = document.createElement('div');
    row.className = message.system ? 'chat-line system' : `chat-line ${message.team}`;
    if (game) row.dataset.at = String(this.arrivedAt.get(message) ?? 0);
    if (!message.system) {
      if (message.scope === 'team') {
        const tag = document.createElement('span');
        tag.className = 'chat-tag';
        tag.textContent = `${t('[Equipo]')} `;
        row.appendChild(tag);
      }
      const name = document.createElement('span');
      name.className = 'chat-name';
      name.textContent = `${message.name}: `;
      row.appendChild(name);
    }
    const text = document.createElement('span');
    text.className = 'chat-text';
    // textContent: nunca HTML del jugador; los avisos del sistema llegan codificados (msg)
    text.textContent = message.system ? tr(message.text) : message.text;
    row.appendChild(text);
    return row;
  }

  /** Con el chat cerrado, cada línea se desvanece pasados unos segundos. */
  private refreshGameFades(): void {
    const now = performance.now();
    for (const row of Array.from(this.gameLog.children) as HTMLElement[]) {
      const age = (now - Number(row.dataset.at ?? 0)) / 1000;
      row.classList.toggle('faded', !this.gameOpen && age > GAME_LINE_SECONDS);
    }
  }
}

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Falta #${id}`);
  return node as T;
}
